import { Box, Typography } from "@mui/material";
import { ProductFormProductPackaging } from "src/pages/Product/ProductForm/ProductForm.types";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { TextLink } from "src/components/Link/TextLink";
import { URLHandler } from "src/services/URLHandler";
import { FoodsIcon, FoodsTextInput } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { canOpenLineItem } from "src/pages/Product/lineItemAccess";
import { formatNumber } from "src/utils/formatting";

type LineTitleProps = {
    packaging: ProductFormProductPackaging;
    setPackaging: (newPackaging: ProductFormProductPackaging) => void;
};

const getPackagingKind = (packaging: ProductFormProductPackaging): "element" | "composite" | null => {
    if (packaging.packagingReference) {
        return packaging.packagingReference.kind;
    }

    if (packaging.packaging?.["@type"] === "PackagingElement") {
        return "element";
    }

    if (packaging.packaging?.["@type"] === "PackagingComposite") {
        return "composite";
    }

    return null;
};

export const LineTitle = (props: LineTitleProps) => {
    const { selectedCompany } = useFoodpilotContext();
    const { packaging, setPackaging } = props;
    const { t, i18n } = useTranslation();

    if (selectedCompany === null) return;

    const packagingName = packaging.packaging?.name ?? packaging.packagingReference?.name ?? "";
    const isElementReference = packaging.packagingReference?.kind === "element";
    const kind = getPackagingKind(packaging);
    const formattedTenantWeight =
        (
            packaging.packaging?.["@type"] === "PackagingElement" &&
            packaging.packaging.weight !== null &&
            packaging.packaging.weight !== undefined
        ) ?
            formatNumber(packaging.packaging.weight, i18n.resolvedLanguage, { maximumFractionDigits: 3 })
        :   null;
    const displayName =
        formattedTenantWeight === null ? packagingName : (
            `${packagingName} (${formattedTenantWeight} ${t("weight.grams", "g")})`
        );
    const value = isElementReference ? packaging.usedWeight : packaging.quantity;
    const expectedValueLabel =
        isElementReference ? t("packaging_element.weight", "Weight (g)") : t("packaging_composite.amount", "Quantity");
    const typeLabel =
        kind === "element" ? t("packaging.create_choice.component.title", "Packaging component")
        : kind === "composite" ? t("packaging.create_choice.unit.title", "Packaging unit")
        : null;

    return (
        <Box
            sx={{
                width: "100%",
                display: "grid",
                gridTemplateColumns: "1fr 140px 200px 125px",
            }}
        >
            <Box display="flex" sx={{ gridColumn: "1", alignItems: "center", gap: 1 }}>
                {kind !== null && typeLabel !== null && (
                    <Box
                        component="span"
                        aria-label={typeLabel}
                        title={typeLabel}
                        sx={{
                            alignItems: "center",
                            color: "text.secondary",
                            display: "inline-flex",
                        }}
                    >
                        <FoodsIcon icon={kind === "element" ? "box" : "boxes"} size={1} />
                    </Box>
                )}
                {packaging.packaging && canOpenLineItem(packaging.packaging) ?
                    <TextLink
                        url={URLHandler.packaging.scores(selectedCompany.id, packaging.packaging.id)}
                        text={displayName}
                    />
                :   displayName}
            </Box>
            <Box sx={{ gridColumn: "2", display: "flex", alignItems: "center" }}>
                <Typography variant="body" color="text.secondary">
                    {expectedValueLabel}
                </Typography>
            </Box>
            <Box sx={{ gridColumn: "3", maxWidth: "150px" }}>
                <FoodsTextInput
                    value={value?.toString() ?? ""}
                    onChange={(value) => {
                        const newPackaging: ProductFormProductPackaging = {
                            ...packaging,
                            ...(isElementReference ?
                                { usedWeight: value === "" ? null : Number(value), quantity: null }
                            :   { quantity: value === "" ? null : Number(value), usedWeight: null }),
                        };
                        setPackaging(newPackaging);
                    }}
                    ExtraInputProps={{
                        type: "number",
                        placeholder: isElementReference ? t("packaging_element.weight", "Weight (g)") : "1",
                    }}
                />
            </Box>
        </Box>
    );
};
